import { inferFieldType } from "../utils/firestoreValueUtils";

const SCHEMA_STORAGE_KEY = "firestoreAdmin.schemas";

export const SCHEMA_FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "timestamp",
  "array",
  "map",
  "null",
];

function loadAll() {
  try {
    const raw = localStorage.getItem(SCHEMA_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistAll(map) {
  try {
    localStorage.setItem(SCHEMA_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota errors */
  }
}

export function listSchemas() {
  return loadAll();
}

export function getSchema(collectionName) {
  if (!collectionName) return null;
  return loadAll()[collectionName] || null;
}

export function saveSchema(collectionName, schema) {
  if (!collectionName) return;
  const map = loadAll();
  map[collectionName] = {
    fields: Array.isArray(schema.fields) ? schema.fields : [],
    allowExtra: schema.allowExtra !== false,
    updatedAt: new Date().toISOString(),
  };
  persistAll(map);
}

export function deleteSchema(collectionName) {
  const map = loadAll();
  delete map[collectionName];
  persistAll(map);
}

/**
 * Build a candidate schema from a sample of documents.
 * A field is marked required when it is present (and non-undefined) in every doc.
 */
export function inferSchemaFromRows(rows) {
  const total = rows.length;
  const stats = new Map();
  rows.forEach((row) => {
    const data = row.data || {};
    Object.keys(data).forEach((field) => {
      if (field === "id") return;
      const type = inferFieldType(data[field]);
      const entry = stats.get(field) || { present: 0, types: {} };
      entry.present += 1;
      entry.types[type] = (entry.types[type] || 0) + 1;
      stats.set(field, entry);
    });
  });

  const fields = [...stats.entries()]
    .map(([name, entry]) => {
      const dominantType = Object.entries(entry.types).sort((a, b) => b[1] - a[1])[0]?.[0] || "string";
      return {
        name,
        type: dominantType,
        required: total > 0 && entry.present === total,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return { fields, allowExtra: true };
}

/**
 * Validate documents against a schema.
 * Returns per-document issues plus aggregate counts.
 */
export function scanDocuments(rows, schema) {
  const fields = schema?.fields || [];
  const allowExtra = schema?.allowExtra !== false;
  const known = new Set(fields.map((f) => f.name));

  const violations = [];
  const issueCounts = { missing_required: 0, type_mismatch: 0, null_value: 0, unexpected_field: 0 };

  rows.forEach((row) => {
    const data = row.data || {};
    const issues = [];

    fields.forEach((field) => {
      const has = Object.prototype.hasOwnProperty.call(data, field.name);
      const value = data[field.name];

      if (!has || value === undefined) {
        if (field.required) {
          issues.push({ field: field.name, kind: "missing_required", detail: "Required field is absent" });
          issueCounts.missing_required += 1;
        }
        return;
      }

      if (value === null) {
        if (field.required && field.type !== "null") {
          issues.push({ field: field.name, kind: "null_value", detail: "Required field is null" });
          issueCounts.null_value += 1;
        }
        return;
      }

      const actual = inferFieldType(value);
      if (field.type && field.type !== "null" && actual !== field.type) {
        issues.push({
          field: field.name,
          kind: "type_mismatch",
          detail: `Expected ${field.type}, found ${actual}`,
        });
        issueCounts.type_mismatch += 1;
      }
    });

    if (!allowExtra) {
      Object.keys(data).forEach((key) => {
        if (key === "id") return;
        if (!known.has(key)) {
          issues.push({ field: key, kind: "unexpected_field", detail: "Field not defined in schema" });
          issueCounts.unexpected_field += 1;
        }
      });
    }

    if (issues.length) violations.push({ id: row.id, issues });
  });

  return {
    scanned: rows.length,
    valid: rows.length - violations.length,
    violations,
    issueCounts,
  };
}

export const ISSUE_LABELS = {
  missing_required: "Missing required",
  type_mismatch: "Type mismatch",
  null_value: "Null value",
  unexpected_field: "Unexpected field",
};
